import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useMakeSelector } from 'hooks/redux'
import { useDispatch } from 'react-redux'
import { isEmpty } from 'ramda'
import * as yup from 'yup'
import strings from 'utils/strings'
import {
  DRAWER_SELECT_MODE,
  APPLY_TEMPLATE_TYPE_CUSTOM,
  APPLY_TEMPLATE_TYPE_DAIMLER,
} from 'constants/ui/ptoDiagram'
import {
  updateTemplateFragments,
  getVehiclePtoTemplateById,
} from 'actions/powerTakeOff'
import {
  getTemplateMetaList,
  makeGetTemplatesSelector,
  getPtoTemplateDiff,
  isPtoTemplateLoaded,
} from 'selectors/powerTakeOff'
import SearchInputV2 from 'components/patterns/Inputs/SearchInputV2'
import { Row, Col, Alert, SelectMenu } from 'components/patterns'
import { ReactComponent as EmptyCompartment } from 'img/Empty-compartment.svg'
import { makeComponentMetaSelector } from 'selectors/navigator.js'
import { getFilteredTemplateList } from './utils.js'
import TemplateCard from './TemplateCard'
import PtoTemplateDiffTable from './PtoTemplateDiffTable'
import PtoTemplateNotes from './PtoTemplateNotes'
import PtoTemplateActionButtons from './PtoTemplateActionButtons'
import {
  ViewWrapper,
  AlertContainer,
  ApplyTemplateWrapper,
  ApplyTemplateFooter,
  BottomContainer,
  StyledSearchSortLabel,
  NoTemplateResults,
} from './ApplyTemplate.style'

const SortOptions = {
  'timeStamp-desc': [
    strings.pto_template_search_sort_label_newest_first,
    (a, b) => new Date(b.timeStamp) - new Date(a.timeStamp),
  ],
  'timeStamp-asc': [
    strings.pto_template_search_sort_label_oldest_first,
    (a, b) => new Date(a.timeStamp) - new Date(b.timeStamp),
  ],
  'title-asc': [
    strings.pto_template_search_sort_label_title_asc,
    (a, b) => a.name.localeCompare(b.name),
  ],
  'title-desc': [
    strings.pto_template_search_sort_label_title_desc,
    (a, b) => b.name.localeCompare(a.name),
  ],
}

const SortMenuOptions = Object.entries(SortOptions).map(([key, [title]]) => ({
  key,
  value: key,
  title,
}))

const [{ value: DefaultSortOption }] = SortMenuOptions

const ApplyTemplate = ({
  toggleTemplateDrawer,
  isDrawerVisible,
  setDrawerState,
  setDrawerTitle,
  templateType,
}) => {
  const viewWrapperScrollerRef = useRef()
  const dispatch = useDispatch()
  const { serialNumber } = useMakeSelector(makeComponentMetaSelector, {
    id: 'ptoTemplatesWrapper',
  })
  const [selectedTemplateId, setSelectedTemplateId] = useState('')
  const [isPtoDiffView, setPtoDiffView] = useState(false)
  const [isNotesView, setNotesView] = useState(false)
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const getTemplates = useMakeSelector(makeGetTemplatesSelector)
  const diffListByTemplateId = useSelector(getPtoTemplateDiff, {
    templateId: selectedTemplateId,
  })
  const isTemplateLoaded = useSelector(isPtoTemplateLoaded, {
    templateId: selectedTemplateId,
  })
  const hasDiff = !isEmpty(diffListByTemplateId)

  const [templatesListFiltered, setTemplatesListFiltered] = useState([])
  const [templateSort, updateTemplateSort] = useState(DefaultSortOption)
  const [templateSearchQuery, updateTemplateSearchQuery] = useState(null)
  const templatesList = useSelector(getTemplateMetaList)

  useEffect(() => {
    const [, sortComparator] = SortOptions[templateSort]
    setTemplatesListFiltered(
      getFilteredTemplateList(
        templatesList,
        templateType,
        templateSearchQuery,
        sortComparator
      )
    )
  }, [templateType, templatesList, templateSearchQuery, templateSort])

  useEffect(() => {
    if (templateType === APPLY_TEMPLATE_TYPE_CUSTOM) {
      setDrawerTitle(strings.apply_pto_title_custom)
    }
    if (templateType === APPLY_TEMPLATE_TYPE_DAIMLER) {
      setDrawerTitle(strings.apply_pto_title_daimler)
    }
  }, [setDrawerTitle, templateType])

  useEffect(() => {
    if (viewWrapperScrollerRef.current && (isNotesView || isPtoDiffView)) {
      viewWrapperScrollerRef.current.scrollTop = 0
    }
  }, [isNotesView, isPtoDiffView])

  const handleDrawerAndClearTemplate = useCallback(() => {
    setDrawerState(DRAWER_SELECT_MODE)
    setSelectedTemplateId('')
    setNotesView(false)
    setPtoDiffView(false)
  }, [setSelectedTemplateId, setNotesView, setDrawerState])

  const handleSelectionAndDiffView = useCallback(
    (id) => {
      setSelectedTemplateId(id)
      dispatch(getVehiclePtoTemplateById(id, serialNumber))
      setPtoDiffView(!isPtoDiffView)
      setNotesView(false)
      setNotes('')
    },
    [dispatch, isPtoDiffView, serialNumber]
  )

  const handleCancelToTemplateView = useCallback(() => {
    setSelectedTemplateId('')
    setNotesView(false)
    setPtoDiffView(false)
  }, [setNotesView, setPtoDiffView])

  const handleSetToNotesView = useCallback(() => {
    setNotesView(true)
    setPtoDiffView(false)
  }, [setNotesView, setPtoDiffView])

  const handleCancelToDiffView = useCallback(() => {
    setNotesView(false)
    setPtoDiffView(!isPtoDiffView)
  }, [setNotesView, setPtoDiffView, isPtoDiffView])

  const handleNotesChange = useCallback(
    (notesVal) => {
      setNotes(notesVal)
    },
    [setNotes]
  )

  const handleApplyWithNotes = useCallback(() => {
    setSubmitting(!submitting)
    dispatch(
      updateTemplateFragments(notes, selectedTemplateId, () => {
        toggleTemplateDrawer(!isDrawerVisible)
        setSubmitting(false)
      })
    )
    setNotes('')
  }, [
    submitting,
    dispatch,
    notes,
    selectedTemplateId,
    toggleTemplateDrawer,
    isDrawerVisible,
  ])

  const handleOnClear = () => {
    updateTemplateSearchQuery('')
  }

  const renderTemplateCards = useMemo(() => {
    if (!isEmpty(templatesListFiltered)) {
      return templatesListFiltered.map((template) => {
        const { templateId } = template

        return (
          <TemplateCard
            key={templateId}
            className={`template-${templateId}`}
            template={template}
            handleTemplateSelection={handleSelectionAndDiffView}
            selectedTemplateId={selectedTemplateId}
            defaultChecked={false}
          />
        )
      })
    }
    return (
      <NoTemplateResults>
        <EmptyCompartment />
        {strings.apply_pto_template_no_templates}
      </NoTemplateResults>
    )
  }, [templatesListFiltered, handleSelectionAndDiffView, selectedTemplateId])

  const showNoDiffMessage = isPtoDiffView && isTemplateLoaded && !hasDiff

  if (isEmpty(getTemplates)) return null

  const isCardsView = !isPtoDiffView && !isNotesView
  const isSearchView = isCardsView

  return (
    <ViewWrapper ref={viewWrapperScrollerRef}>
      {showNoDiffMessage && (
        <AlertContainer>
          <Alert
            type="info"
            message={strings.no_diff_apply_pto_template}
            persistent
          />
        </AlertContainer>
      )}
      {isNotesView && (
        <AlertContainer>
          <Alert
            type="warning"
            message={strings.apply_pto_banner_message}
            persistent
          />
        </AlertContainer>
      )}
      <ApplyTemplateWrapper isCardsView>
        {isSearchView && (
          <Row
            type="inline"
            justify="space-between"
            style={{ marginLeft: 0, marginRight: 0, marginBottom: 25 }}
          >
            <SearchInputV2
              onClear={handleOnClear}
              value={templateSearchQuery}
              onChange={(e) => updateTemplateSearchQuery(e.target.value)}
              placeholder={
                templateType === APPLY_TEMPLATE_TYPE_CUSTOM
                  ? strings.pto_template_search_placeholder_custom
                  : strings.pto_template_search_placeholder
              }
              width={400}
            />
            <Row
              type="inline"
              justify="space-around"
              style={{ marginLeft: 0, marginRight: 0 }}
            >
              <StyledSearchSortLabel>
                {strings.pto_template_search_sort_by}
              </StyledSearchSortLabel>
              <SelectMenu
                options={SortMenuOptions}
                onChange={(v) => {
                  if (v) updateTemplateSort(v)
                }}
                name="templateSortOptions"
                value={templateSort}
                rules={yup.string().nullable()}
                horizontal
                width={100}
              />
            </Row>
          </Row>
        )}

        {isCardsView && (
          <Row type="inline" style={{ margin: -8 }} justify="space-between">
            {renderTemplateCards}
          </Row>
        )}

        {isPtoDiffView && (
          <BottomContainer data-testid="pto-template-diff-table">
            <Col span={24}>
              <PtoTemplateDiffTable selectedTemplateId={selectedTemplateId} />
            </Col>
          </BottomContainer>
        )}

        {isNotesView && (
          <BottomContainer>
            <PtoTemplateNotes
              selectedTemplateId={selectedTemplateId}
              handleNotesChange={handleNotesChange}
              notes={notes}
            />
          </BottomContainer>
        )}
      </ApplyTemplateWrapper>

      <Row align="bottom">
        <Col span={24}>
          <ApplyTemplateFooter>
            <PtoTemplateActionButtons
              selectedTemplateId={selectedTemplateId}
              isPtoDiffView={isPtoDiffView}
              isNotesView={isNotesView}
              notes={notes}
              submitting={submitting}
              handleNotesChange={handleNotesChange}
              handleDrawerAndClearTemplate={handleDrawerAndClearTemplate}
              handleSetToNotesView={handleSetToNotesView}
              handleCancelToTemplateView={handleCancelToTemplateView}
              handleCancelToDiffView={handleCancelToDiffView}
              handleApplyWithNotes={handleApplyWithNotes}
            />
          </ApplyTemplateFooter>
        </Col>
      </Row>
    </ViewWrapper>
  )
}

ApplyTemplate.propTypes = {
  serialNumber: PropTypes.string,
  toggleTemplateDrawer: PropTypes.func,
  isDrawerVisible: PropTypes.bool,
  templateType: PropTypes.string,
  setDrawerTitle: PropTypes.func,
  setDrawerState: PropTypes.func,
}

export default ApplyTemplate
